import {
  CreateShipmentWorkflow as ShipmentDto,
  LegacyQRCodeShipment as LegacyQRCodeShipmentDto,
  AddressValueObject,
  UPSResponseModel as UpsResponseDto,
} from '@electricjs/core_entity-client';

import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import {
  GetOrganizationShipmentsArgs,
  LegacyQRCodeShipment,
  Shipment,
  ShipmentCreationArgs,
} from '@/types/shipments';
import { ApiListResponseDto } from '@externalTypes/general';
import { PaginatedResponse } from '@/types/common';

// Exported for unit testing — builds the snake_case API payload from the
// camelCase `Shipment` form data. `asset_id`/`destination_site_id` are
// optional on the DTO, so they're only included when actually applicable
// (an asset was selected / shipping to a customer site) rather than sent as
// `undefined`. `ship_to_electric_warehouse` signals a warehouse shipment;
// the backend is expected to resolve the warehouse's site, address, and
// phone itself instead of receiving them from the frontend. The
// create-shipment endpoint doesn't support this field at runtime yet
// (tracked separately, though the field is now declared on the generated
// `ShipmentDto` as of @electricjs/core_entity-client 24.293.1); do not ship
// this to production until backend support lands, or warehouse shipments
// will submit with no receiver data. `receiver_name`/`receiver_phone`/
// `receiver_address` are optional on both `Shipment` and the DTO (omitted
// only for a warehouse shipment), but empty/blank fallbacks are sent rather
// than omitting the keys, for a predictable wire shape — the backend
// ignores them once it resolves the warehouse destination on its own.
export const buildShipmentDto = (shipment: Shipment): ShipmentDto => ({
  sender_name: shipment.senderName,
  sender_email: shipment.senderEmail,
  sender_phone: shipment.senderPhone,
  sender_address: {
    street_address_1: shipment.senderAddress.streetAddress1,
    street_address_2: shipment.senderAddress.streetAddress2 ?? undefined,
    city: shipment.senderAddress.city,
    state: shipment.senderAddress.state,
    country: shipment.senderAddress.country,
    zip_code: shipment.senderAddress.zip,
  },
  device_name: shipment.deviceName,
  serial_number: shipment.serialNumber,
  receiver_name: shipment.receiverName ?? '',
  receiver_phone: shipment.receiverPhone ?? '',
  receiver_address: {
    street_address_1: shipment.receiverAddress?.streetAddress1 ?? '',
    street_address_2: shipment.receiverAddress?.streetAddress2 ?? undefined,
    city: shipment.receiverAddress?.city ?? '',
    state: shipment.receiverAddress?.state ?? '',
    country: shipment.receiverAddress?.country ?? '',
    zip_code: shipment.receiverAddress?.zip ?? '',
  },
  administrator_email: shipment.administratorEmail,
  shipment_origin: shipment.shipmentOrigin,
  ...(shipment.assetId ? { asset_id: shipment.assetId } : {}),
  ...(shipment.destinationSiteId
    ? { destination_site_id: shipment.destinationSiteId }
    : {}),
  ...(shipment.shipToElectricWarehouse
    ? { ship_to_electric_warehouse: true }
    : {}),
});

/**
 * The carrier's delivery date as an ISO date or timestamp, or undefined when it
 * is missing or in a shape we can't read (so callers show "not recorded"
 * rather than a wrong date). Accepts UPS-style compact dates (YYYYMMDD).
 */
export const normalizeDeliveryDate = (value?: string): string | undefined => {
  if (!value) return undefined;
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(value);
  const normalized = compact
    ? `${compact[1]}-${compact[2]}-${compact[3]}`
    : value;
  return isNaN(new Date(normalized).getTime()) ? undefined : normalized;
};

export const shipmentApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    /**
     * Carrier delivery date (`delivery_date`) per tracking number. A number the
     * carrier has no delivery date for maps to undefined.
     */
    getDeliveryDates: builder.query<
      Record<string, string | undefined>,
      { trackingNumbers: string[] }
    >({
      queryFn: async ({ trackingNumbers }, _api, _extraOptions, baseQuery) => {
        const results = await Promise.all(
          trackingNumbers.map(trackingNumber =>
            baseQuery({
              url: `/v1/shipments/tracking/${encodeURIComponent(trackingNumber)}`,
            })
          )
        );

        const failed = results.find(result => result.error);
        if (failed?.error) {
          return { error: failed.error };
        }

        return {
          data: Object.fromEntries(
            trackingNumbers.map((trackingNumber, index) => [
              trackingNumber,
              normalizeDeliveryDate(
                (results[index].data as UpsResponseDto).delivery_date
              ),
            ])
          ),
        };
      },
    }),
    createShipment: builder.mutation<void, ShipmentCreationArgs>({
      query: ({ organizationId, shipment }) => ({
        url: `v1/organizations/${organizationId}/shipments`,
        method: 'POST',
        body: buildShipmentDto(shipment),
      }),
      invalidatesTags: [TagTypes.Shipment],
    }),
    getOrganizationShipments: builder.query<
      PaginatedResponse<LegacyQRCodeShipment>,
      GetOrganizationShipmentsArgs
    >({
      query: ({ organizationId, limit, offset, statuses }) => {
        const params = new URLSearchParams();
        if (limit !== undefined) {
          params.append('limit', String(limit));
        }
        if (offset !== undefined) {
          params.append('offset', String(offset));
        }
        if (statuses && statuses.length > 0) {
          statuses.forEach(status => {
            params.append('statuses', status);
          });
        }

        const queryString = params.toString();
        return {
          url: `v1/organizations/${organizationId}/shipments${
            queryString ? `?${queryString}` : ''
          }`,
          method: 'GET',
        };
      },
      transformResponse: (
        response: ApiListResponseDto<LegacyQRCodeShipmentDto>
      ): PaginatedResponse<LegacyQRCodeShipment> => {
        return {
          results: response.results.map(shipment => ({
            id: shipment.id,
            organizationId: shipment.organization_id,
            status: shipment.status,
            upsTrackingNumber: shipment.ups_tracking_number,
            assetId: shipment.asset_id,
            deviceName: shipment.device_name,
            deviceSerialNumber: shipment.device_serial_number,
            employeeName: shipment.shipper_employee_name,
            employeeEmail: shipment.shipper_employee_email,
            administratorUpdateEmail: shipment.administrator_update_email,
            shipFromAddressString: shipment.ship_from_address_string,
            shipFromAddress: transformShipmentAddress(
              shipment.ship_from_address
            ),
            shipToAddressString: shipment.ship_to_address_string,
            shipToAddress: transformShipmentAddress(shipment.ship_to_address),
            shipToElectricWarehouse: shipment.ship_to_electric_warehouse,
            createdAt: shipment.created_at,
            updatedAt: shipment.updated_at,
            expiresAt: shipment.expires_at,
          })),
          total: response.total ?? 0,
        };
      },
      providesTags: [TagTypes.Shipment],
    }),
  }),
});

const transformShipmentAddress = (address: AddressValueObject | undefined) => {
  if (!address || !Object.values(address).some(value => value)) {
    return undefined;
  }

  return {
    streetAddress1: address.street_1,
    streetAddress2: address.street_2 ?? '',
    city: address.city,
    state: address.state,
    country: address.country,
    zip: address.zip_code ?? '',
  };
};

// export auto-generated hooks
export const {
  useCreateShipmentMutation,
  useGetDeliveryDatesQuery,
  useGetOrganizationShipmentsQuery,
} = shipmentApiSlice;
